import React, { useState, useEffect } from "react";
import Layout from "../../layouts";
import DynamicTable from "../../components/DynamicTable";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";
import { formatDateTimeAmORPm } from "../../utils/common";
import UserDetailsModal from "../../components/UserDetails";
import { withSearchIcon } from "../../utils/helper";

interface Host {
  id: number;
  created_at?: string;
  updated_at?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_no?: string;
  gender?: string;
  dob?: string;
  profile_photo?: string;
  occupation?: string;
  height?: string;
  country?: string;
  city?: string;
  last_seen?: string;
  active?: boolean;
  visible?: boolean;
  last_visited?: string;
  blocked?: boolean;
  blocker_id?: number;
  blocker_type?: string;
  profile_status?: string;
  is_logged_in?: boolean;
  login_source?: string;
  meeting_purpose?: string;
  interested_in_gender?: string;
  [key: string]: any;
}

export default function HostList() {
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState<Host[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const [processing, setProcessing] = useState(false);

  // 🟢 New: Search State
  const [searchTerm, setSearchTerm] = useState("");
  const [activeUserTab, setActiveUserTab] = useState("Personal Details");

  useEffect(() => {
    setActiveUserTab("Personal Details");
  }, []);

  const [viewModal, setViewModal] = useState<{
    open: boolean;
    user?: Host;
  }>({ open: false });

  const [subscriberModal, setSubscriberModal] = useState<{
    open: boolean;
    hostId?: number;
    data: any[];
    loading: boolean;
    hostName?: string;
  }>({
    open: false,
    hostId: undefined,
    data: [],
    loading: false,
  });

  const [selectedHost, setSelectedHost] = useState<any>({});
  const [subscriberPage, setSubscriberPage] = useState(1);
  const [subscriberTotalPages, setSubscriberTotalPages] = useState(1);
  const [subscriberTotalRecords, setSubscriberTotalRecords] = useState(0);
  const [viewModalFromSubscribers, setViewModalFromSubscribers] =
    useState(false);
  const [searchSubscriberTerm, setSearchSubscriberTerm] = useState("");

  useEffect(() => {
    fetchUsers(page, searchTerm);
  }, [page, searchTerm]); // 🟢 refetch when search changes

  // ✅ Fetch Host List
  const fetchUsers = async (activePageNo: number, search: string = "") => {
    try {
      setLoading(true);
      const params = { page: activePageNo, limit: 50, search }; // 🟢 send search to API
      const response: any = await apiService.get(
        "/users/get-host-users",
        params
      );
      setUsers(response?.data?.users || []);
      setTotalRecords(response?.data?.totalUserCount || 0);
      setTotalPages(response?.data?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Table Columns
  const columns = [
    { key: "id", label: "User ID" },
    { key: "first_name", label: withSearchIcon("First Name") },
    { key: "last_name", label: withSearchIcon("Last Name") },
    {
      key: "host_alias",
      label: withSearchIcon("Organization Name"),
      render: (val: string) => (val ? val : "N/A"),
    },
    { key: "email", label: withSearchIcon("Email") },
    { key: "phone_no", label: "Phone No" },
    {
      key: "gender",
      label: "Gender",
      render: (val: string) => {
        let badgeClass = "px-2 py-1 rounded-full text-xs font-semibold ";
        badgeClass +=
          val === "Men"
            ? "bg-blue-100 text-blue-800"
            : val === "Women"
            ? "bg-pink-100 text-pink-800"
            : val === "Non-Binary"
            ? "bg-purple-100 text-purple-800"
            : "bg-gray-100 text-gray-800";
        return <span className={badgeClass}>{val || "N/A"}</span>;
      },
    },
    {
      key: "profile_photo",
      label: "Profile Photo",
      render: (val: string) =>
        val ? (
          <div className="flex items-center gap-2">
            <img
              src={val}
              alt="Profile"
              className="w-10 h-10 rounded-full object-cover border"
            />
            <span
              onClick={() => {
                navigator.clipboard.writeText(val);
                toast.success("Image URL copied!");
              }}
              className="text-xs text-blue-500 hover:underline cursor-pointer"
            >
              Copy URL
            </span>
          </div>
        ) : (
          "N/A"
        ),
    },

    {
      key: "blocked",
      label: "Status",
      render: (val: boolean) => {
        let badgeClass = "px-2 py-1 rounded-full text-xs font-semibold ";
        badgeClass += val=== true
          ? "bg-red-100 text-red-800"
          : "bg-green-100 text-green-800";
        return <span className={badgeClass}>{val === true ? "Blocked" : "Active"}</span>;
      },
    },
    {
      key: "actions",
      label: "Actions",
      className:
        "px-4 py-3 whitespace-nowrap text-right sticky right-0 z-10 rounded space-x-2 bg-white/30 backdrop-blur-md shadow-md",
      render: (_: any, row: Host) => (
        <div className="flex gap-2">
          {/* 👁‍🗨 View Button */}
          <button
            onClick={() => handleOpenViewFromHostList(row)}
            className="px-2 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded w-[5rem]"
          >
            View
          </button>
          <button
            onClick={() => {
              openSubscribersModal(row.id);
              setSelectedHost(row);
              setSubscriberPage(1);
            }}
            className="px-2 py-1.5 text-xs bg-green-600 hover:bg-green-700 text-white rounded w-[5rem]"
          >
            Subscribers
          </button>
        </div>
      ),
    },
  ];

  const subscribersColumns = [
    { key: "id", label: "User ID" },
    { key: "first_name", label: withSearchIcon("First Name") },
    { key: "last_name", label: withSearchIcon("Last Name") },
    {
      key: "host_alias",
      label: withSearchIcon("Organization Name"),
      render: (val: string) => (val ? val : "N/A"),
    },
    { key: "email", label: withSearchIcon("Email") },
    { key: "phone_no", label: "Phone No" },
    {
      key: "gender",
      label: "Gender",
      render: (val: string) => {
        let badgeClass = "px-2 py-1 rounded-full text-xs font-semibold ";
        badgeClass +=
          val === "Men"
            ? "bg-blue-100 text-blue-800"
            : val === "Women"
            ? "bg-pink-100 text-pink-800"
            : val === "Non-Binary"
            ? "bg-purple-100 text-purple-800"
            : "bg-gray-100 text-gray-800";
        return <span className={badgeClass}>{val || "N/A"}</span>;
      },
    },
    {
      key: "profile_photo",
      label: "Profile Photo",
      render: (val: string) =>
        val ? (
          <div className="flex items-center gap-2">
            <img
              src={val}
              alt="Profile"
              className="w-10 h-10 rounded-full object-cover border"
            />
            <span
              onClick={() => {
                navigator.clipboard.writeText(val);
                toast.success("Image URL copied!");
              }}
              className="text-xs text-blue-500 hover:underline cursor-pointer"
            >
              Copy URL
            </span>
          </div>
        ) : (
          "N/A"
        ),
    },

    {
      key: "blocked",
      label: "Status",
      render: (val: boolean) => {
        let badgeClass = "px-2 py-1 rounded-full text-xs font-semibold ";
        badgeClass += val === true
          ? "bg-red-100 text-red-800"
          : "bg-green-100 text-green-800";
        return <span className={badgeClass}>{val === true ? "Blocked" : "Active"}</span>;
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: (_: any, row: Host) => (
        <button
          onClick={() => {
            setViewModalFromSubscribers(true); // Mark origin
            setSubscriberModal((prev) => ({ ...prev, open: false })); // Close modal only
            openViewModal(row); // Open user view
          }}
          className="px-2 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded w-[5rem]"
        >
          View
        </button>
      ),
    },
  ];

  const openViewModal = (user: Host) => {
    setViewModal({ open: true, user });
    setActiveUserTab("Personal Info");
  };

  const openSubscribersModal = async (
    hostId: number,
    page: number = 1,
    search: string = ""
  ) => {
    setSubscriberModal((prev) => ({
      ...prev,
      open: true,
      hostId,
      loading: true,
    }));
    // setLoading(true);
    const params = {
      page: subscriberPage,
      limit: 50,
      hostId: hostId,
      search: search ? search : searchSubscriberTerm,
    }; // 🟢 send search to API
    const response: any = await apiService.get(
      "/users/get-subscribed-users",
      params
    );

    setSubscriberModal((prev) => ({
      ...prev,
      data: response?.data?.users || [],
      loading: false,
    }));
    setSubscriberTotalRecords(response?.data?.totalCount || 0);
    setSubscriberTotalPages(response?.data?.totalPages || 1);
    // setLoading(false);
  };

  const handleOpenViewFromHostList = (host: Host) => {
    setViewModalFromSubscribers(false);
    openViewModal(host);
  };

  return (
    <Layout pageTitle="Host List">
      <div className="min-h-screen bg-gray-50 py-3 px-4 min-w-0">
        <DynamicTable
          data={users}
          columns={columns}
          pageSize={50}
          loading={loading}
          title="Host List"
          activeNUmber={page}
          onPageChange={(newPage) => setPage(newPage)}
          totalRecords={totalRecords}
          totalPages={totalPages}
          onSearch={(value: string) => {
            // 🟢 Listen to DynamicTable search input
            setPage(1); // reset to first page
            setSearchTerm(value); // update search key
          }}
        />
      </div>

      {/* VIEW USER DETAILS MODAL */}
     

      <UserDetailsModal
          open={viewModal.open}
          user={viewModal.user}
          onClose={() => setViewModal({ open: false })}
          loading={false}
          title={viewModalFromSubscribers && subscriberModal.hostId
                  ? "Subscriber Details"
                  : "Host Details"}
        />

      {subscriberModal.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-[90vw] rounded-lg shadow-xl p-6 max-h-[98vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">
                Subscribers — {selectedHost?.first_name}{" "}
                {selectedHost?.last_name} (ID:{subscriberModal.hostId})
              </h2>

              <button
                onClick={() =>
                  setSubscriberModal({
                    open: false,
                    data: [],
                    loading: false,
                  })
                }
                className="w-8 h-8 flex items-center justify-center 
            rounded-full hover:bg-red-200 
            transition text-red-600 hover:text-red-900"
              >
                ✕
              </button>
            </div>

            {/* TABLE */}
            <DynamicTable
              key="Subscribers"
              data={subscriberModal.data}
              columns={subscribersColumns}
              loading={subscriberModal.loading}
              pageSize={50}
              title="Subscribers"
              activeNUmber={subscriberPage}
              totalPages={subscriberTotalPages}
              totalRecords={subscriberTotalRecords}
              onPageChange={(newPage: number) => {
                if (subscriberModal.hostId) {
                  openSubscribersModal(
                    subscriberModal.hostId,
                    newPage,
                    searchSubscriberTerm
                  );
                }
                setSubscriberPage(newPage);
              }}
              onSearch={(value: string) => {
                if (subscriberModal.hostId) {
                  // setSearchSubscriberTerm(value);
                  setSubscriberPage(1);

                  openSubscribersModal(
                    subscriberModal.hostId,
                    1,
                    value && value?.length > 0 ? value : ""
                  );
                  // setSearchSubscriberTerm(value?value: "")
                }
              }}
            />
          </div>
        </div>
      )}
    </Layout>
  );
}
